import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { TooltipModule } from 'primeng/tooltip';

import { AppStateHolder, TimeLine } from '../../../../shared';
import { UserActivitiesService } from '../user-activities.service';

/** Interface for tracking state statistics for accessibility */
interface StateStatistics {
  opened: number;
  working: number;
  closed: number;
}

@Component({
    selector: 'state-chart',
    templateUrl: 'activity-stateChart.component.html',
    standalone: true,
    imports: [TooltipModule, ChartModule, DatePipe, TranslateModule]
})

export class ActivityStateChartComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private appHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private tralsator = inject(TranslateService);
  private service = inject(UserActivitiesService);

  /** Reference to the accessible data table for focus management */
  @ViewChild('stateDataTable') stateDataTable: ElementRef<HTMLTableElement>;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  requestsSubscribe: Subscription;
  status: string;
  activities: any[];
  dataOfState: any;
  graphOptions: any;

  /** State statistics for accessibility data table */
  stateStats: StateStatistics = { opened: 0, working: 0, closed: 0 };

  openOptions: boolean = false;
  optionSelected: any;

  finalDate: string;
  initialDate: string;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

  }

  ngOnInit(): void {
    if (!this.modify) {
      this.timeLine.init();
      this.loadActivities(this.params);
    }
  }

  loadActivities(params: string) {
    this.status = 'busy';
    if (params) {
      let prms = JSON.parse(params);

      prms = prms.params ? JSON.parse(prms.params) : prms;

      if (prms.date == 'Day')
        this.dataClick(0);
      if (prms.date == 'Week')
        this.dataClick(1);
      if (prms.date == 'Month')
        this.dataClick(2);
      if (prms.date == 'all')
        this.dataClick(1);

      this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, "All", prms.typologies)
        .subscribe(res => {
          this.status = 'ready';
          this.activities = res;
          this.stateStatics();

        })
    }
    else {
      this.dataClick(1);
      this.service
        .getUserActivitiesFil(this.initialDate, this.finalDate, "All", null)
        .subscribe(result => {

          this.status = 'ready';
          this.activities = result;
          this.stateStatics();
        });
    }
  }

  hasActivities(): Boolean {

    if (this.activities === undefined)
      return false;

    return (this.activities.length > 0);
  }

  stateStatics() {

    let opened = this.activities.filter((state) => {
      return state.workingState == 0
    })
    let working = this.activities.filter((state) => {
      return state.workingState == 1
    })
    let closed = this.activities.filter((state) => {
      return state.workingState == 2
    })
    let stats = [];

    stats.push(opened.length, working.length, closed.length,);

    // Store state statistics for accessibility data table
    this.stateStats = {
      opened: opened.length,
      working: working.length,
      closed: closed.length
    };

    this.dataOfState = {
      labels: [this.tralsator.instant("chart-view.label-opened"), this.tralsator.instant("chart-view.label-working"), this.tralsator.instant("chart-view.label-closed"),],
      datasets: [
        {
          data: stats,
          backgroundColor: [
            "#ff6964",
            "#FFCE56",
            "#5ce85c",
          ],
          hoverBackgroundColor: [
            "#ff6964",
            "#FFCE56",
            "#5ce85c",
          ]
        }

      ]
    }
    this.graphOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10
        }
      },
      legend: {
        display: true,
        position: 'right',
        labels: {
          fontSize: 14,
          padding: 14
        }
      }
    };
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {

    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appHolder.stateGraphParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {

    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "/" + (day.getMonth() + 1) + "/" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "/" + (d1.getMonth() + 1) + "/" + d1.getDate();
        this.finalDate = d2.getFullYear() + "/" + (d2.getMonth() + 1) + "/" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "/" + (this.timeLine.month.date.getMonth() + 1) + "/" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "/" + (this.timeLine.month.lastDate.getMonth() + 1) + "/" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Focuses the accessible data table for screen reader users.
   * This method is called when users activate the skip link.
   */
  focusDataTable(): void {
    if (this.stateDataTable?.nativeElement) {
      this.stateDataTable.nativeElement.focus();
    }
  }
}
